import * as _ from "lodash";

import { ExtensionBase } from "./extensionBase";
import { SdEvents, ModelNode } from "../smartDrawings";
import { UiUtils } from "../common/uiUtils";
import { viewerOptions } from "../viewer";
import { ViewerUtils } from "../common/viewerUtils";

/*
*   Helper class for storing model button state internally
*/
class ModelButton {
    public button       : Autodesk.Viewing.UI.Button;
    public model        : Autodesk.Viewing.Model;
    public node         : number;
    public description  : string;

    constructor(uiButton : Autodesk.Viewing.UI.Button,
        model : Autodesk.Viewing.Model, node : number, description : string) {

        this.button         = uiButton;
        this.model          = model;
        this.node           = node;
        this.description    = description;
    }
}

// Duplicated from InstanceTree.js
const NODE_TYPE = {
    NODE_TYPE_ASSEMBLY   : 0x0,
    NODE_TYPE_INSERT     : 0x1,
    NODE_TYPE_LAYER      : 0x2,
    NODE_TYPE_COLLECTION : 0x3,
    NODE_TYPE_COMPOSITE  : 0x4,
    NODE_TYPE_MODEL      : 0x5,
    NODE_TYPE_GEOMETRY   : 0x6,
    NODE_TYPE_BITS       : 0x7
};

const ModelType = {
    Single      : 0,
    CombiSd     : 1,
    CombiNwd    : 2
};

/*
*   Menu for hiding/showing single models of a combi model.
*/
export class CombiModelTreeExtension extends ExtensionBase {
    static extensionName = "CombiModelTreeExtension";

    private static readonly maxMenuItemLength = 15;

    private subToolbar : Autodesk.Viewing.UI.ControlGroup;
    private combiModelTreeButton : Autodesk.Viewing.UI.ComboButton;
    private modelType : number;

    // Internal array of model-button instances
    private modelButtons : Array<ModelButton>;

    constructor(viewer: Autodesk.Viewing.GuiViewer3D, options: any) {
        super(viewer, options);

        this.modelButtons = [];
    }

    static registerExtension() {
        super.registerExtension(
            CombiModelTreeExtension.extensionName, CombiModelTreeExtension);
    }

    load(): boolean {
        console.log(`${CombiModelTreeExtension.extensionName} is loaded!`);

        this.viewer.addEventListener(
            SdEvents.SD_OBJECT_TREE_CREATED, () => this.onAllModelsLoaded());

        return super.load();
    }

    unload(): boolean {
        return super.unload();
    }

    onToolbarCreated() {
    }

    private createToolbar() {
        this.combiModelTreeButton = UiUtils.createComboButton({
            id: "SmartDrawings.CombiModelTreeButton",
            tooltip: "Show/hide models",
            iconClass: "ion-md-eye",
            onClick: () => {}
        });

        this.combiModelTreeButton.setState(Autodesk.Viewing.UI.Button.State.INACTIVE);

        this.subToolbar = this.getControlGroup("SmartDrawings.Extensions");
        this.subToolbar.addControl(this.combiModelTreeButton);
    }

    private onAllModelsLoaded() {
        this.viewer.removeEventListener(
            SdEvents.SD_OBJECT_TREE_CREATED, () => this.onAllModelsLoaded());

        this.updateModelType();
        if (this.modelType === ModelType.Single)
            return;

        this.createToolbar();
        this.createMenuItems();
    }

    private updateModelType() {
        // SD combi model
        // Multiple models, one model node (root) per model
        if (viewerOptions.comboModelId !== undefined) {
            this.modelType = ModelType.CombiSd;
            return;
        }

        // NWD model
        // One model, model has root node + model nodes for each model
        const tree = this.viewer.model.getInstanceTree();
        let models = 0;
        for (let n = 0; n < tree.nodeAccess.numNodes; n++) {
            if (tree.getNodeType(n) != NODE_TYPE.NODE_TYPE_MODEL)
                continue;

            models++;
            if (models === 2) {
                this.modelType = ModelType.CombiNwd;
                return;
            }
        }

        this.modelType = ModelType.Single;
    }

    private createMenuItemsFromSdCombi() {
        const models = this.viewer.getAllModels();
        const combiModels = viewerOptions.models;

        for (let n = 0; n < models.length; n++) {
            const name = combiModels[n].name;
            this.addButton(models[n], models[n].getRootId(), name);
        }
    }

    private createMenuItemsFromNwd() {
        const model = this.viewer.model;
        const tree = model.getInstanceTree();
        const rootNode = tree.getRootId();

        for (let n = 0; n < tree.nodeAccess.numNodes; n++) {
            if (n === rootNode || tree.getNodeType(n) !== NODE_TYPE.NODE_TYPE_MODEL)
                continue;

            this.addButton(model, n, tree.getNodeName(n));
        }
    }

    private createMenuItems() {
        switch (this.modelType) {
        case ModelType.CombiNwd:
            this.createMenuItemsFromNwd();
            break;
        case ModelType.CombiSd:
            this.createMenuItemsFromSdCombi();
            break;
        default:
            return;
        }

        this.sortButtons();
    }

    /*
    *   Sort buttons alphabetically
    *   Does NOT affect internal button list order, only UI button order
    */
    private sortButtons() {
        // Remove buttons from UI & stash to list
        const buttons : Array<Autodesk.Viewing.UI.Button> = [];
        for (const modelButton of this.modelButtons) {
            buttons.push(modelButton.button);
            this.combiModelTreeButton.removeControl(modelButton.button);
        }

        // Order alphabetically
        const names = buttons.map(button => button.getId());
        names.sort();

        // Add buttons back to UI in new order
        for (const buttonName of names) {
            const modelButton = this.modelButtons.find(
                findButton => findButton.button.getId() === buttonName);

            this.combiModelTreeButton.addControl(modelButton.button);
        }
    }

    private toggleNodeState(model : Autodesk.Viewing.Model, node : number, show : boolean) {
        ViewerUtils.setModelNodeVisibility(model, show, this.viewer);

        if (!show) return;

        const hiddenNodes = viewerOptions.hiddenNodes as Array<ModelNode>;

        // Hide hidden nodes when showing model
        for (const hiddenNode of hiddenNodes) {
            if (hiddenNode.modelId !== model.id) continue;

            (this.viewer.impl.visibilityManager as any)
                .setNodeOff(hiddenNode.nodeId, true, model);

            this.viewer.hide(hiddenNode.nodeId, model);
        }
    }

    private async onButtonClick(model : Autodesk.Viewing.Model, node : number) {
        // Get respective modelbutton from internal array
        for (let modelButton of this.modelButtons) {
            if (modelButton.model !== model || modelButton.node !== node)
                continue;

            const show = !this.viewer.isNodeVisible(node, model);

            this.toggleNodeState(model, node, show);
            this.updateButton(modelButton.button, show, modelButton.description);
        }

         // Prevent menu from closing when toggling model states
         const menu = this.combiModelTreeButton as any;
         menu.toggleFlyoutVisible();
    }

    private addButton(model : Autodesk.Viewing.Model, node : number, description : string) {
        const button : any = UiUtils.createButton({
            id: description,
            onClick: async () => this.onButtonClick(model, node)
        });

        const show = this.viewer.isNodeVisible(node, model);
        this.updateButton(button, show, description);
        button.addClass("toolbar-text-button");

        this.combiModelTreeButton.addControl(button);
        this.modelButtons.push(new ModelButton(button, model, node, description));
    }

    private updateButton(button : Autodesk.Viewing.UI.Button, show : boolean, description : string) {
        const colorVisibleText      = "255, 255, 255, 255";
        const colorInvisibleText    = "164, 164, 164, 255";

        const colorText = show ? colorVisibleText : colorInvisibleText;
        const buttonAny = button as any;

        // Truncate
        if (description.length > CombiModelTreeExtension.maxMenuItemLength) {
            description = description.substring(
                0, CombiModelTreeExtension.maxMenuItemLength - 3) + "...";
        }

        description = _.escape(description);

        const icon = show
            ? "ion-md-eye"
            : "ion-md-eye-off";
        
        const nodeStyle = "";

        const iconHtml =
            `<div class="${nodeStyle} icon ${icon}" style="margin-right: 10px"></div>`;

        const textHtml =
            `<div style="width: max-content; color: rgba(${colorText})">${description}</div>`;
        
        buttonAny.container.innerHTML = iconHtml + textHtml;  
    }
}
